import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import Chart from '../chart';
import { useReducedMotion } from 'framer-motion';

export default function WeeklyKpiChart() {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState([]);
  const [target, setTarget] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drilldown, setDrilldown] = useState(null); // { week_start, week_end, tags: [] }
  const [drillTags, setDrillTags] = useState([]);
  const [drillSearch, setDrillSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const { theme } = useTheme();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const shouldReduce = useReducedMotion();

  useEffect(() => {
    setLoading(true);
    fetch(`/api/kpi.php?action=weekly&weeks=${weeks}`)
      .then((r) => r.json())
      .then((d) => {
        setData(d.weeks || []);
        setTarget(d.target || 0);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [weeks]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const labels = data.map((w) => formatLabel(w.week_start, w.week_end));
    const values = data.map((w) => w.unique_tag_count);
    chartRef.current?.destroy();
    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            type: 'bar',
            data: values,
            backgroundColor: '#036EC8',
            borderRadius: 6,
          },
          {
            type: 'line',
            label: `Target = ${target}`,
            data: new Array(values.length).fill(target),
            borderColor: '#ff0000',
            borderWidth: 2,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: shouldReduce ? false : { duration: 500, easing: 'easeOutQuart' },
        onClick: (_, elements) => {
          if (elements.length) {
            const idx = elements[0].index;
            setDrilldown(data[idx]);
            setDrillSearch('');
            setPage(1);
          }
        },
        plugins: {
          legend: {
            labels: {
              color: theme === 'dark' ? '#e5e7eb' : '#374151',
            },
          },
          tooltip: {
            callbacks: {
              title: (ctx) => labels[ctx[0].dataIndex],
              label: (ctx) => {
                const count = ctx.parsed.y;
                const pct = target ? ((count / target) * 100).toFixed(1) : '0.0';
                return [`Unique tags: ${count}`, `Target: ${target}`, `% vs target: ${pct}%`];
              },
            },
          },
        },
        scales: {
          x: {
            ticks: { color: theme === 'dark' ? '#e5e7eb' : '#374151' },
            grid: { color: theme === 'dark' ? '#374151' : '#e5e7eb' },
          },
          y: {
            beginAtZero: true,
            ticks: { color: theme === 'dark' ? '#e5e7eb' : '#374151' },
            grid: { color: theme === 'dark' ? '#374151' : '#e5e7eb' },
          },
        },
      },
    });
    return () => chartRef.current?.destroy();
  }, [data, target, theme, shouldReduce]);

  useEffect(() => {
    if (!drilldown) return;
    const params = new URLSearchParams({
      week_start: drilldown.week_start,
      week_end: drilldown.week_end,
      page: page.toString(),
    });
    if (drillSearch) params.append('q', drillSearch);
    fetch(`/api/kpi.php?action=week_tags&${params.toString()}`)
      .then((r) => r.json())
      .then((d) => {
        setDrillTags(d.rows || []);
        setTotalPages(d.total_pages || 1);
      });
  }, [drilldown, drillSearch, page]);

  const formatLabel = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const opts = { day: 'numeric', month: 'short' };
    const sStr = s.toLocaleDateString(undefined, opts);
    const eStr = e.toLocaleDateString(undefined, opts);
    return `${sStr}–${eStr}`;
  };

  const exportSummary = () => {
    window.location = `/api/kpi.php?action=weekly_export&weeks=${weeks}`;
  };

  const exportDrill = () => {
    if (!drilldown) return;
    const params = new URLSearchParams({
      week_start: drilldown.week_start,
      week_end: drilldown.week_end,
    });
    if (drillSearch) params.append('q', drillSearch);
    window.location = `/api/kpi.php?action=week_tags_export&${params.toString()}`;
  };

  return (
    <div className="bg-header text-default border border-border rounded-lg p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium">Weekly KPI's</h2>
        <div className="flex items-center space-x-2">
          <select
            value={weeks}
            onChange={(e) => setWeeks(Number(e.target.value))}
            className="p-1 rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 text-sm"
          >
            <option value={4}>Last 4 weeks</option>
            <option value={12}>Last 12 weeks</option>
            <option value={26}>Last 26 weeks</option>
            <option value={52}>Last 52 weeks</option>
          </select>
          <button
            onClick={exportSummary}
            className="px-2 py-1 text-sm bg-primary text-white rounded focus:outline-none focus:ring-2 focus:ring-focus hover:bg-primary-hover"
          >
            Export CSV
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-muted">Loading...</div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center text-red-600">Failed to load</div>
      ) : data.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-muted">
          No data available. <button className="ml-2 text-primary" onClick={() => setWeeks(12)}>Change filters</button>
        </div>
      ) : (
        <div className="relative flex-1">
          <canvas ref={canvasRef} className="w-full h-full" />
        </div>
      )}
      {drilldown && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true"
        >
          <div className="bg-header text-default border border-border rounded-lg max-w-lg w-full max-h-full overflow-auto p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">
                Tags for {formatLabel(drilldown.week_start, drilldown.week_end)}
              </h3>
              <button
                onClick={() => setDrilldown(null)}
                className="focus:outline-none focus:ring-2 focus:ring-focus rounded"
              >
                Close
              </button>
            </div>
            <div>
              <input
                value={drillSearch}
                onChange={(e) => { setDrillSearch(e.target.value); setPage(1); }}
                placeholder="Search tags"
                className="w-full p-1 rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600"
              />
            </div>
            <ul className="space-y-2">
              {drillTags.map((t) => (
                <li key={t.tag_name} className="border-b border-border pb-1">
                  <div className="font-medium">{t.tag_name}</div>
                  {t.first_seen && (
                    <div className="text-xs text-muted">
                      {new Date(t.first_seen).toLocaleString()} - {new Date(t.last_seen).toLocaleString()}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between text-sm">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-2 py-1 bg-primary text-white rounded disabled:opacity-50"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-2 py-1 bg-primary text-white rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={exportDrill}
                className="px-2 py-1 text-sm bg-primary text-white rounded focus:outline-none focus:ring-2 focus:ring-focus hover:bg-primary-hover"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}